import { TemplateResult, html, LitElement } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import encapsulatedStyles from './styles/outline-jump-nav.encapsulated.pcss?inline';
import { MobileController } from '../../controllers/mobile-controller';
import { AdoptedStyleSheets } from '../../controllers/adopted-stylesheets';
import '../outline-mobile-menu/outline-mobile-menu';
import '../outline-container/outline-container';

export type OutlineJumpNavJumps = { [key: string]: string };
export type OutlineJumpNavVisibility = { [key: string]: number };
export type OutlineJumpNavLoadingStates = 'failed' | 'succeeded' | 'loading';

/**
 * The OutlineJumpNav component
 * @element outline-jump-nav
 */
@customElement('outline-jump-nav')
export class OutlineJumpNav extends LitElement {
  adoptedStyleSheets = new AdoptedStyleSheets(this, {
    encapsulatedCSS: encapsulatedStyles,
  });

  resizeObserver: ResizeObserver;
  mobileController = new MobileController(this);

  /**
   * ID of "active" element.
   */
  @property({ type: String })
  isActive: string;

  /**
   * ID or classname of navigation or any kind of sticky element the jump nav may need to remain positioned below.
   */
  @property({ type: String })
  nav: string;

  /**
   * ID prefix for targeted sections.
   */
  @property({ type: String })
  slug: string = 'jump-link--';

  /**
   * Expected link IDs and their titles.
   */
  @property({ type: Object })
  jumps: OutlineJumpNavJumps = {};

  /**
   * Ref to the desktop ul element
   */
  @query('.outline-jump-nav--list')
  ul: HTMLElement;

  /**
   * Default label text for the mobile button when not fixed, or no active link.
   */
  @property({ type: String })
  mobileLabelDefault: string = 'View Provider Information';

  /**
   * The current label text for the mobile button. Reflects he current active link.
   */
  @property({ type: String, reflect: true })
  mobileLabel: string;

  /**
   * Ref to the mobile select element
   */
  @query('outline-mobile-menu')
  mobileMenu: HTMLElement;

  /**
   * Indicates if the component is first loading.
   */
  @property({ type: String })
  status: OutlineJumpNavLoadingStates = 'loading';

  /**
   * Tracks mobile state
   */
  @property({ type: Boolean, attribute: true, reflect: true })
  isMobile: boolean;

  /**
   * Wether or not the nav is in 'fixed' position.
   */
  @property({ type: Boolean, reflect: true })
  fixed: boolean;

  /**
   * Current height of the jump-nav. Used to determine true viewable space.
   */
  jumpNavHeightOffset: number;

  /**
   * Current height of the main-nav. Used to determine true viewable space.
   */
  headerOffset: number;

  /**
   * Height of any hero on the page/the position that the jump-nav is supposed to switch to fixed/sticky position.
   */
  triggerFixedOffset: number;

  /**
   * If the users browser is set to 'prefers-reduced-motion' will prevent scrolling and cleanly jump to selected section.
   */
  preventScroll: boolean;

  render(): TemplateResult {
    return html`<section class="outline-jump-nav">
      <div class="outline-jump-nav--container">
        ${this.mobileController.isMobile
          ? this.mobileTemplate()
          : this.desktopTemplate()}
      </div>
    </section>`;
  }

  desktopTemplate() {
    return html`
      <div class="outline-jump-nav--wrapper">
        <nav class="outline-jump-nav--nav" aria-label="jump navigation">
          <ul class="outline-jump-nav--list"></ul>
        </nav>
      </div>
    `;
  }

  mobileTemplate() {
    return html` <div class="outline-jump-nav--wrapper">
      <outline-mobile-menu
        svg-size="30px"
        close-icon="circleCross"
        open-icon="circleMinus"
        isJumpnav
      >
        <span slot="button-label">${this.mobileLabel}</span>
        <ul class="outline-jump-nav--list"></ul>
      </outline-mobile-menu>
    </div>`;
  }

  firstUpdated() {
    this.mobileLabel = this.mobileLabelDefault;
    this.mobileDesktopHandler();
    this.handleJumpInitialization().then(() => {
      this.toggleMarkup();
      this.handleInitialHashScroll();
    });
    this.setOffsets();
    this.setReduceMotion();
    this.targetObserver();
    this.resizeObserver.observe(this);
  }

  updated() {
    this.setOffsets();
  }

  connectedCallback(): void {
    super.connectedCallback();
    this.resizeObserver = new ResizeObserver(() => {
      this.setOffsets();
      this.mobileDesktopHandler();
    });
    window.addEventListener('scroll', this.toggleFixedPosition);
  }

  disconnectedCallback(): void {
    window.removeEventListener('scroll', this.toggleFixedPosition);
    this.resizeObserver.unobserve(this);
    this.cleanupObservers();
    super.disconnectedCallback();
  }

  handleInitialHashScroll() {
    const hash = window.location.hash;
    if (hash && hash.startsWith('#')) {
      // Wait to allow DOM to render
      setTimeout(() => {
        const target = document.querySelector(hash);
        if (target) {
          target.scrollIntoView({
            behavior: this.preventScroll ? 'auto' : 'smooth',
          });
        }
      }, 300);
    }
  }

  targetObserver() {
    window.addEventListener('scroll', this.scrollPositionHandler);
  }

  cleanupObservers() {
    const targetContainers = document.querySelectorAll(`[id^="${this.slug}"]`);
    targetContainers.forEach(con =>
      con.removeEventListener('scroll', this.scrollPositionHandler)
    );
  }
  /**
   * Handles setting the active element on manual scrolling.
   * Currently sets element to active when ~15% from top of the viewport.
   */
  scrollPositionHandler() {
    const host = document.querySelector('outline-jump-nav') as OutlineJumpNav;
    const targetContainers = document.querySelectorAll(`[id^="${host.slug}"]`);
    if (host) {
      targetContainers.forEach(con => {
        if (
          getComputedStyle(con).display !== 'none' &&
          (con.getBoundingClientRect().top <= window.innerHeight * 0.25 ||
          con.getBoundingClientRect().bottom <= window.innerHeight * -0.8)
        ) {
          host.setActive(con.id);
          if (host.isMobile) {
            const dataLabel = con.getAttribute('data-title');
            if (dataLabel && dataLabel !== null) {
              // @ts-expect-error because ts
              host.mobileLabel = con.getAttribute('data-title');
            }
          }
        }
      });
    }
  }

  /**
   * Manages the mobile state and toggles the markup at desktop/mobil swap.
   */
  mobileDesktopHandler() {
    if (this.isMobile === undefined) {
      this.isMobile = this.mobileController.isMobile;
      return this.isMobile;
    }
    if (this.isMobile !== this.mobileController.isMobile) {
      this.toggleMarkup();
      this.isMobile = !this.isMobile;
      return this.isMobile;
    } else {
      return;
    }
  }

  /**
   * If user has prefers reduced motion set, prevents scrolling behavior and jumps the page to the link.
   */
  setReduceMotion() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.preventScroll = true;
    }
  }

  /**
   * Finds all elements with an id that begins with the slug, and creates the jumps object
   * { element id: link text }
   */
  initializeJumps() {
    const targetedElements = document.querySelectorAll(`[id^="${this.slug}"]`);
    if (!targetedElements.length) {
      return false;
    } else {
      targetedElements.forEach(element => {
        const name = element.getAttribute('data-title');
        if (name) {
          this.jumps[`${element.id}`] = name;
        } else {
          this.jumps[`${element.id}`] = 'NO TITLE ENTERED';
        }
      });
      return true;
    }
  }

  /**
   * Deals with error catching on jump object creation.
   */
  async handleJumpInitialization() {
    const completed = await this.initializeJumps();

    if (completed) {
      return (this.status = 'succeeded');
    } else {
      return (this.status = 'failed');
    }
  }

  /**
   * Sets the css var for the nav's fixed position.
   */
  setTopVar(height: number) {
    this.style.setProperty('--top-offset', `${height}px`);
  }

  /**
   * Generates links from this.jumps object.
   */
  generateLinks() {
    Object.entries(this.jumps).forEach(jump => {
      const li = document.createElement('li');
      const anchor = document.createElement('a');

      li.classList.add('outline-jump-nav--item');
      li.classList.add(`${jump[0]}`);
      li.addEventListener('click', this.scrollHandler);

      anchor.classList.add('outline-jump-nav--link');
      anchor.id = `${jump[0]}-jump`;
      anchor.setAttribute('href', `#${jump[0]}`);
      anchor.setAttribute('aria-label', `scroll to ${jump[1]}`);

      // insert your preferred string processing here
      anchor.innerText = jump[1];
      li.appendChild(anchor);
      this.ul.appendChild(li);
    });
  }

  generateErrorMessage() {
    this.jumps = { name: 'ERROR NO LINKS' };
    this.generateLinks();
  }

  /**
   * Generates correct markup depending on screen width. Forces setActive to make sure all styles are toggled.
   */
  toggleMarkup() {
    if (this.status === 'failed') {
      return this.generateErrorMessage();
    }
    if (this.status === 'succeeded') {
      this.generateLinks();
    }
    if (this.isActive) {
      this.setActive(this.isActive);
    }
    this.targetObserver();
  }

  /**
   * On click/change "scroll handler" to initiate scrolling.
   */
  scrollHandler(e: Event) {
    e.preventDefault();
    const host = document.querySelector('outline-jump-nav') as OutlineJumpNav;
    let target;
    let targetHref;
    let navHeight = 0;
    if (host.isMobile) {
      target = e.target as HTMLAnchorElement;
      targetHref = target.getAttribute('href');
      host.mobileMenu.removeAttribute('open');
      host.mobileLabel = target.innerText;
      navHeight = host.fixed
        ? host.jumpNavHeightOffset + host.headerOffset
        : host.jumpNavHeightOffset + 60 + host.headerOffset;
    }
    if (!host.isMobile) {
      target = e.target as HTMLAnchorElement;
      targetHref = target.getAttribute('href');
      navHeight = host.fixed
        ? host.jumpNavHeightOffset + host.headerOffset
        : host.jumpNavHeightOffset * 2 + host.headerOffset;
    }

    const scrollTarget = document.querySelector(`${targetHref}`) as HTMLElement;
    if (scrollTarget) {
      const parentOffset =
        scrollTarget.offsetParent !== null
          ? (scrollTarget.offsetParent as HTMLElement).offsetTop
          : 0;
      const correctedTop = scrollTarget.offsetTop + parentOffset - navHeight;
      window.scroll({
        top: correctedTop,
        behavior: host.preventScroll ? 'auto' : 'smooth',
      });
    }
  }

  /**
   * Called when the component reaches/exceeds triggerFixedOffset point and applies/removes fixed attribute.
   */
  toggleFixedPosition() {
    const host = document.querySelector('outline-jump-nav') as OutlineJumpNav;
    if (host.getBoundingClientRect().y <= host.triggerFixedOffset + 10) {
      host.fixed = true;
    }
    if (host.getBoundingClientRect().y > host.triggerFixedOffset + 10) {
      host.fixed = false;
      host.mobileLabel = host.mobileLabelDefault;
      host.setActive('');
    }
  }

  /**
   * Determines headerOffset, topOffset, and triggerFixedOffset properties for use in calculations.
   */
  setOffsets() {
    const header =
      !!this.nav && this.nav !== '' ? document.querySelector(this.nav) : false;
    const headerHeight = header ? header.getBoundingClientRect().height : 0;
    const nav = document
      .querySelector('outline-jump-nav')
      ?.shadowRoot?.querySelector('.outline-jump-nav--wrapper');
    this.jumpNavHeightOffset = nav ? nav.getBoundingClientRect().height : 0;
    this.headerOffset = headerHeight;
    this.triggerFixedOffset = headerHeight !== 0 ? headerHeight : 0;
    this.setTopVar(this.headerOffset);
  }

  /**
   * Takes an ID and if not already the active ID, sets it as this.isActive, then handles the passing of the active-jump class to the correct link.
   * The force argument is used when the component switches between mobile and desktop to make sure the active class is applied.
   */
  setActive(id: string) {
    if (this.isActive !== id) {
      this.isActive = id;
      this.shadowRoot
        ?.querySelector(`.active-jump`)
        ?.classList.remove('active-jump');
      this.shadowRoot
        ?.querySelector(`#${id}-jump`)
        ?.classList.add('active-jump');
    }
  }
}
declare global {
  interface HTMLElementTagNameMap {
    'outline-jump-nav': OutlineJumpNav;
  }
}
